import React, { useState } from "react";
import axios from "axios";
import { Loader2, CreditCard, Shield } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Pulsante riusabile per avviare un pagamento Nexi XPay.
 * Props:
 *   amountEur (number, required)
 *   description (string)
 *   email (string, required)
 *   customerName (string)
 *   tipoPolizza (string) - es. "auto", "casa", "vita", "nlt", "viaggio"
 *   submissionId (string) - collega pagamento a una submission CRM
 *   numContratto (string) - parametro custom configurato in Nexi backoffice
 *   label (string) - testo pulsante (default: "Paga ora con Nexi")
 *   className (string) - classi tailwind aggiuntive
 *   onError (fn) - callback errore
 */
export default function NexiPaymentButton({
  amountEur,
  description = "Pagamento polizza",
  email = "",
  customerName = "",
  tipoPolizza = "generico",
  submissionId = "",
  numContratto = "",
  label,
  className = "",
  disabled = false,
  onError,
}) {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const handlePay = async () => {
    setErr("");
    if (!amountEur || amountEur < 0.5) {
      setErr("Importo non valido");
      return;
    }
    if (!email) {
      setErr("Email richiesta");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/nexi/initiate`, {
        amount_eur: amountEur,
        description,
        email,
        customer_name: customerName,
        tipo_polizza: tipoPolizza,
        submission_id: submissionId,
        num_contratto: numContratto,
      });
      if (data?.redirect_url) {
        // Persist cod_trans per recupero su return page
        try {
          sessionStorage.setItem(
            "nexi_last_tx",
            JSON.stringify({
              cod_trans: data.cod_trans,
              amount_eur: data.amount_eur,
              at: Date.now(),
            })
          );
        } catch (_) {}
        window.location.href = data.redirect_url;
      } else {
        throw new Error("Risposta non valida dal server");
      }
    } catch (e) {
      const msg =
        e?.response?.data?.detail ||
        e?.message ||
        "Errore avvio pagamento. Riprova.";
      setErr(msg);
      if (onError) onError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-stretch gap-2">
      <button
        type="button"
        onClick={handlePay}
        disabled={disabled || loading}
        data-testid="nexi-pay-button"
        className={
          "group relative overflow-hidden rounded-full px-8 py-4 font-semibold text-base " +
          "bg-gradient-to-r from-amber-500 via-amber-400 to-amber-500 text-slate-900 " +
          "hover:from-amber-400 hover:to-amber-300 " +
          "shadow-[0_8px_28px_-6px_rgba(245,158,11,0.55)] hover:shadow-[0_10px_32px_-4px_rgba(245,158,11,0.75)] " +
          "disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-300 " +
          "flex items-center justify-center gap-3 " +
          className
        }
      >
        {loading ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>Reindirizzamento a Nexi…</span>
          </>
        ) : (
          <>
            <CreditCard className="w-5 h-5" />
            <span>
              {label || `Paga €${Number(amountEur || 0).toFixed(2)} con Nexi`}
            </span>
          </>
        )}
      </button>
      {err ? (
        <p className="text-red-400 text-sm font-medium" data-testid="nexi-pay-error">
          {err}
        </p>
      ) : null}
      <p className="text-xs text-slate-400 flex items-center gap-1.5 justify-center">
        <Shield className="w-3.5 h-3.5" />
        Pagamento sicuro Nexi XPay · Circuiti Visa, Mastercard, AmEx
      </p>
    </div>
  );
}
